import { flatten, get } from 'lodash';
import PropTypes from 'prop-types';

import Loader from 'hb-react/shared/components/Loader';
import useFetchAppPages from 'hb-react/shared/hooks/useFetchAppPages';
import { usePageConfigQuery } from 'hb-react/shared/hooks/usePageConfigQuery';

export const getAppPageConfig = (
  identifier,
  currentNetwork,
  appPages = null,
) => {
  let config = {};

  const getConfig = (pages) => {
    // native pages can be within submenus
    const pagesWithSubMenus = flatten(
      pages.map((page) => [page, ...(page?.submenus ?? [])]),
    ).filter(Boolean);

    return pagesWithSubMenus.find(
      (page) =>
        (page.identifier || page.menu?.identifier) === identifier ||
        (page.feature_name || page.menu?.feature_name) === identifier,
    );
  };

  if (appPages) {
    config = getConfig(appPages);
  } else {
    currentNetwork = currentNetwork || AlumniConnect.currentNetwork;

    if (currentNetwork && currentNetwork.app_pages) {
      config = getConfig(currentNetwork.app_pages);
    }
  }

  return config?.menu ?? config;
};

export const WithAppPageConfig = ({
  children,
  identifier,
  loader = undefined,
}) => {
  const appPages = useFetchAppPages();
  const appPageConfig = getAppPageConfig(identifier, null, appPages);

  const { data: page } = usePageConfigQuery({
    identifier,
    enabled: !appPageConfig?.config,
  });

  if (!appPages) {
    return loader ? (
      <Loader full /> // eslint-disable-line react/jsx-filename-extension
    ) : null;
  }

  const requiresFetchPage = /(forum|media_center|news|ventures)/.test(
    identifier,
  );

  if (requiresFetchPage && !page) {
    return loader ? (
      <Loader full /> // eslint-disable-line react/jsx-filename-extension
    ) : null;
  }

  return children({ appPageConfig: page ?? appPageConfig });
};

WithAppPageConfig.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  identifier: PropTypes.string,
  loader: PropTypes.node,
};

export const getPageTitle = ({ featureName, title, currentNetwork }) => {
  const realTitle = [title];

  currentNetwork = currentNetwork || AlumniConnect.currentNetwork;

  if (featureName) {
    realTitle.push(
      get(getAppPageConfig(featureName, currentNetwork), 'menu_name'),
    );
  }
  if (currentNetwork) {
    realTitle.push(currentNetwork.page_title);
  }

  return realTitle.filter(Boolean).join(' | ') || document.title;
};

export const getFeaturePage = (pages, featureName) =>
  pages?.find((page) => page.menu.feature_name === featureName) || {};

export const getFeatureMenuCustomName = (
  featureName,
  defaultName,
  appPages = [],
) => {
  const menu = getAppPageConfig(featureName, null, appPages);

  return menu?.custom_name ? menu.custom_name : defaultName;
};
